import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Loader2, CheckCircle2, LogOut, RefreshCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Auth.css';

const CheckEmailPage = () => {
  const navigate = useNavigate();
  const { user, loading, authedFetch, logout, refreshUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(null);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const pollRef = useRef(null);

  // Route guards
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.email_verified) {
      navigate('/app', { replace: true });
    }
  }, [user, loading, navigate]);

  // Auto-poll /me every 10s in case user verified from another tab / mobile
  useEffect(() => {
    if (!user || user.email_verified) return;
    pollRef.current = setInterval(() => { refreshUser(); }, 10000);
    return () => clearInterval(pollRef.current);
  }, [user, refreshUser]);

  // Resend cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    setError('');
    setSending(true);
    try {
      const res = await authedFetch('/api/auth/resend-verification', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not resend the verification email.');
      }
      setSentAt(new Date());
      setCooldown(30);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  if (loading || !user) {
    return (
      <div className="auth-page">
        <div className="auth-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
          <Loader2 size={32} className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-background">
        <video className="auth-video" autoPlay loop muted playsInline>
          <source src="/hero_video.mp4" type="video/mp4" />
        </video>
        <div className="auth-video-overlay"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card" style={{ maxWidth: 520 }} data-testid="check-email-page">
          <div className="auth-header" style={{ textAlign: 'center' }}>
            <Link to="/" className="auth-logo" style={{ justifyContent: 'center' }}>
              <img
                src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
                alt="Ezai Build"
                className="logo-icon"
                style={{ width: 44, height: 44 }}
              />
              <span className="logo-text">Ezai Build</span>
            </Link>

            <div style={{
              margin: '32px auto 20px',
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'rgba(0, 255, 209, 0.08)',
              border: '1px solid rgba(0, 255, 209, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Mail size={32} color="var(--brand-primary)" />
            </div>

            <h1 className="display-medium auth-title">Check your email</h1>
            <p className="body-medium auth-subtitle" style={{ maxWidth: 420, margin: '12px auto 0' }}>
              We sent a verification link to
            </p>
            <p className="body-medium" style={{ color: 'var(--text-primary)', fontWeight: 600, marginTop: 4 }} data-testid="user-email">
              {user.email}
            </p>
            <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: 16 }}>
              Click the link in that email to verify your address and unlock your account.
              <br />The link expires in 24 hours.
            </p>
          </div>

          {sentAt && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 14px',
              background: 'rgba(0, 255, 209, 0.06)',
              border: '1px solid rgba(0, 255, 209, 0.25)',
              borderRadius: 8,
              margin: '20px 0 0',
              justifyContent: 'center',
            }} data-testid="resend-success">
              <CheckCircle2 size={16} color="var(--brand-primary)" />
              <span className="body-small">A fresh verification email is on its way.</span>
            </div>
          )}

          {error && (
            <p className="auth-error body-small" data-testid="auth-error" style={{ marginTop: 16 }}>{error}</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
            <Button
              className="btn-primary btn-large auth-btn"
              onClick={handleResend}
              disabled={sending || cooldown > 0}
              data-testid="resend-verification-btn"
            >
              {sending ? (
                <Loader2 size={20} className="spinner" />
              ) : cooldown > 0 ? (
                `Resend in ${cooldown}s`
              ) : (
                <>
                  <RefreshCcw size={18} />
                  <span style={{ marginLeft: 8 }}>Resend verification email</span>
                </>
              )}
            </Button>

            <button
              type="button"
              onClick={handleSignOut}
              className="body-small"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '8px',
              }}
              data-testid="check-email-signout-btn"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>

          <p className="body-small" style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 20 }}>
            Didn't get it? Check your spam folder, or try resending after 30 seconds.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckEmailPage;
